import {
  BiometricsResponse,
  GetBiometricsArgs,
  useGetBiometricsQuery,
} from 'services/biometricsService';

interface UseBiometricsOptions {
  /** yyyy-MM-dd */
  startDate?: string;
  /** yyyy-MM-dd */
  endDate?: string;
}

interface BiometricResult {
  data?: BiometricsResponse;
  isLoading: boolean;
  /** true while (re)fetching, including when args change and `data` is still the previous window's */
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
}

// Fetches all three biometric types for a user; each query is skipped until userId is known.
export const useBiometrics = (userId?: number, options: UseBiometricsOptions = {}) => {
  const baseArgs: Omit<GetBiometricsArgs, 'biometricType'> = {
    userId: userId ?? 0,
    startDate: options.startDate,
    endDate: options.endDate,
  };
  const skip = userId == null;

  const weightQuery = useGetBiometricsQuery({ ...baseArgs, biometricType: 'weight' }, { skip });
  const bloodPressureQuery = useGetBiometricsQuery(
    { ...baseArgs, biometricType: 'blood_pressure' },
    { skip },
  );
  const bloodGlucoseQuery = useGetBiometricsQuery(
    { ...baseArgs, biometricType: 'blood_glucose' },
    { skip },
  );

  const toResult = (query: typeof weightQuery): BiometricResult => ({
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  });

  return {
    weight: toResult(weightQuery),
    bloodPressure: toResult(bloodPressureQuery),
    bloodGlucose: toResult(bloodGlucoseQuery),
  };
};
